import { Dispatch, SetStateAction, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { WorkflowTemplateOrganisationAccessLevel } from "../../../../../constants";
import { GetWorkflowTemplateResponse } from "../../../../../service/query";
import { useAuth } from "../../../../../useAuth";
import { getOperationsDealWorkflowsRoute, getWorkflowTemplatesRoute } from "../../../../../utils/routes";
import { ProfileMenuItemType } from "../../../../../widget";
import { WorkflowTemplateStepModel } from "./components";
import { WorkflowTemplateContext } from "./WorkflowTemplateContext";

interface useWorkflowTemplateReturnData {
  workflowTemplateUuid: string | undefined;
  workflowTemplate: GetWorkflowTemplateResponse | undefined;
  workflowTemplatePhases: {
    keyDates: string[];
    phase: string;
    sequence: number;
  }[];
  workflowTemplateOrganisationAccessLevel: WorkflowTemplateOrganisationAccessLevel | undefined;
  workflowTemplateOrganisationAccessLevelTooltip: string;
  workflowTemplateSteps: WorkflowTemplateStepModel[] | undefined;

  showEditWorkflowTemplatePanel: boolean;
  setShowEditWorkflowTemplatePanel: Dispatch<SetStateAction<boolean>>;

  showAddWorkflowTemplateStepPanel: boolean;
  setShowAddWorkflowTemplateStepPanel: Dispatch<SetStateAction<boolean>>;

  showApplyWorkflowTemplatePanel: boolean;
  setShowApplyWorkflowTemplatePanel: Dispatch<SetStateAction<boolean>>;

  projectPhase: string;
  handlePhaseChange: (phase: string) => void;

  quickActionsMenuOptionsList: ProfileMenuItemType[];
  showQuickActionsMenu: boolean;
  setShowQuickActionsMenu: Dispatch<SetStateAction<boolean>>;

  hasAccessLevelAndCanEditTemplate: (accessLevel: WorkflowTemplateOrganisationAccessLevel) => boolean;
  shouldRefreshWorkflowTemplate: () => void;
  handleBackClick: () => void;
  goBackText: string;
}

const getworkflowTemplateOrganisationAccessLevelTooltip = (
  workflowTemplateOrganisationAccessLevel: WorkflowTemplateOrganisationAccessLevel | undefined
): string => {
  switch (workflowTemplateOrganisationAccessLevel) {
    case WorkflowTemplateOrganisationAccessLevel.Owner:
      return "As an owner, you can create, edit, and delete anything in the template. You can also apply it to a project and manage who has access.";
    case WorkflowTemplateOrganisationAccessLevel.Author:
      return "As an author, you can create and edit all parts of the template, and apply it to a project. You can’t delete it or manage access rights.";
    case WorkflowTemplateOrganisationAccessLevel.Contributor:
      return "As a contributor, you can add steps and tasks, and edit or delete the ones you’ve created.";
    case WorkflowTemplateOrganisationAccessLevel.Viewer:
    default:
      return "As a viewer, you can see the workflow template, but you can’t make any changes.";
  }
};

export const useWorkflowTemplate = (): useWorkflowTemplateReturnData => {
  const navigate = useNavigate();
  const state = useLocation();
  const { currentUserType } = useAuth();
  const {
    workflowTemplateUuid,
    workflowTemplate,
    workflowTemplatePhases,
    workflowTemplateOrganisationAccessLevel,
    workflowTemplateSteps,
    projectPhase,
    handlePhaseChange,
    shouldRefreshWorkflowTemplate,
    hasAccessLevelAndCanEditTemplate,
  } = useContext(WorkflowTemplateContext);

  const goBackText = state?.state?.origin?.goBackText || "Back to workflows";
  const [showEditWorkflowTemplatePanel, setShowEditWorkflowTemplatePanel] = useState(false);
  const [showAddWorkflowTemplateStepPanel, setShowAddWorkflowTemplateStepPanel] = useState(false);
  const [showApplyWorkflowTemplatePanel, setShowApplyWorkflowTemplatePanel] = useState(false);
  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
  const applyWorkflowTemplateLabel = `Apply to ${workflowTemplate?.type.toLowerCase() ?? "project"}`;

  const quickActionsMenuOptionsList: ProfileMenuItemType[] = [];

  if (hasAccessLevelAndCanEditTemplate(WorkflowTemplateOrganisationAccessLevel.Author)) {
    quickActionsMenuOptionsList.push(
      {
        id: 1,
        value: "Edit template",
        action: () => {
          setShowEditWorkflowTemplatePanel(true);
        },
      },
      {
        id: 2,
        value: applyWorkflowTemplateLabel,
        action: () => setShowApplyWorkflowTemplatePanel(true),
      }
    );
  } else if (workflowTemplate?.isGeneral) {
    quickActionsMenuOptionsList.push({
      id: 1,
      value: applyWorkflowTemplateLabel,
      action: () => setShowApplyWorkflowTemplatePanel(true),
    });
  }
  const handleBackClick = (): void => {
    if (state?.state?.origin?.from) navigate(state?.state?.origin?.from);
    else
      navigate(
        workflowTemplate?.projectType
          ? getWorkflowTemplatesRoute(currentUserType)
          : getOperationsDealWorkflowsRoute(currentUserType)
      );
  };
  return {
    workflowTemplateUuid,
    workflowTemplate,
    workflowTemplatePhases,
    workflowTemplateOrganisationAccessLevel,
    workflowTemplateOrganisationAccessLevelTooltip: getworkflowTemplateOrganisationAccessLevelTooltip(
      workflowTemplateOrganisationAccessLevel
    ),
    workflowTemplateSteps,

    showEditWorkflowTemplatePanel,
    setShowEditWorkflowTemplatePanel,

    showAddWorkflowTemplateStepPanel,
    setShowAddWorkflowTemplateStepPanel,

    showApplyWorkflowTemplatePanel,
    setShowApplyWorkflowTemplatePanel,

    projectPhase,
    handlePhaseChange,

    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,

    hasAccessLevelAndCanEditTemplate,
    shouldRefreshWorkflowTemplate,

    handleBackClick,
    goBackText,
  };
};
